import { Injectable, inject } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { catchError, debounceTime, from, map, mergeMap, of } from 'rxjs'
import { InterventionService } from '../../../../../shared/interventions/services/intervention.service'
import {
  addIntervention,
  addInterventionFailure,
  addInterventionSuccess,
  deleteIntervention,
  deleteInterventionError,
  deleteInterventionSuccess,
  loadInterventions,
  loadInterventionsFailure,
  loadInterventionsSuccess,
  updateIntervention,
  updateInterventionFailure,
  updateInterventionSuccess,
} from '../actions/intervention.actions'

@Injectable()
export class InterventionEffects {
  private actions$ = inject(Actions)
  private interventionService = inject(InterventionService)

  public getInterventionsByCropId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadInterventions),
      debounceTime(100),
      mergeMap((action) =>
        this.interventionService.getByCropId(action.cropId).pipe(
          map((interventionsResponse) =>
            loadInterventionsSuccess({
              cropId: action.cropId,
              interventions: interventionsResponse,
            }),
          ),
          catchError((e) => of(loadInterventionsFailure({ error: `${e}` }))),
        ),
      ),
    ),
  )

  public addIntervention$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addIntervention),
      mergeMap(({ payload }) =>
        this.interventionService.save(payload.intervention, payload.task, payload.documents, payload.pictures).pipe(
          mergeMap((response) =>
            from(response).pipe(
              map((intervention) =>
                addInterventionSuccess({
                  payload: {
                    cropId: intervention.crops?.[0]?.id ?? payload.cropId,
                    intervention,
                  },
                }),
              ),
            ),
          ),
          catchError((e) =>
            of(
              addInterventionFailure({
                error: `${e}`,
                message: `An error occurred during intervention creation`,
              }),
            ),
          ),
        ),
      ),
    ),
  )

  public updateIntervention$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateIntervention),
      mergeMap(({ payload }) =>
        this.interventionService.save(payload.intervention, payload.task, payload.documents, payload.pictures).pipe(
          map(([interventionResponse]) =>
            updateInterventionSuccess({
              payload: {
                cropId: payload.cropId,
                intervention: interventionResponse,
                message: 'Intervention successfully updated',
                loading: false,
              },
            }),
          ),
          catchError((e) => of(updateInterventionFailure({ error: `${e}`, loading: false }))),
        ),
      ),
    ),
  )

  public deleteIntervention$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteIntervention),
      mergeMap(({ cropId, interventionId }) =>
        this.interventionService.delete(interventionId).pipe(
          map(() => deleteInterventionSuccess({ cropId, interventionId })),
          catchError((e) => of(deleteInterventionError({ error: `${e}` }))),
        ),
      ),
    ),
  )
}
